import { useCallback } from 'react';

import {
  defaultRedirectPath,
  defaultRewardsPath,
  IDENTITY_WIDGET_BASEPATH,
  IDENTITY_WIDGET_PATHS,
  REWARDS_MARRIOTT_LINKING_PATHS,
} from '../utils/constants';
import { actually } from '../utils/logging';
import { clearPersistedParameters, getPersistedParameters } from '../utils/persistParameterContext';
import { isBetMGMClient } from '../utils/redirect';
import { getOrigin, getUrlParams } from '../utils/url';

const getDefaultRedirectPath = () => {
  return REWARDS_MARRIOTT_LINKING_PATHS.includes(window.location.pathname) ? defaultRewardsPath : defaultRedirectPath;
};

export const getRedirectUrl = (queryParams?: Record<string, string>) => {
  const params = getPersistedParameters();
  let defaultRedirect = `${window.location.origin}${getDefaultRedirectPath()}`;

  if (params.partner && params.tokenKey) {
    const redirect = new URL(`${window.location.origin}/account/auth/login`);
    redirect.searchParams.append('path', `${defaultRedirectPath}?errorCode=tier_${params.partner}_Success`);
    defaultRedirect = redirect.toString();
  }

  /**
   * AuthSDK's goToSignIn --> getRedirectQueryString provides client_id, redirect_uri, scopes by default in URL query params,
   * and optional deep linked params provided by client apps
   */
  const { redirect_uri = defaultRedirect, client_id: _clientId, scopes: _scopes, ...deepLinkedParams } = params;

  let redirectUri = defaultRedirect;
  let context: string | undefined = undefined;

  try {
    /**
     * TODO: investigate why this is needed all of a sudden
     *
     * The URL is stored encoded which is causing this line to fail.
     * Added decodeURIComponent to workaround the problem
     *
     * E.g. https%3A%2F%2Fdev.devtest.vegas%2Fbook-room%2Fdates%2F - incorrect
     */
    let tmpRedirect;
    const urlParams = getUrlParams();
    if (isBetMGMClient(urlParams.clientId) || redirect_uri !== decodeURIComponent(redirect_uri)) {
      tmpRedirect = new URL(decodeURIComponent(redirect_uri));
    } else {
      tmpRedirect = new URL(redirect_uri);
    }

    if (deepLinkedParams) {
      Object.keys(deepLinkedParams).forEach(key => tmpRedirect.searchParams.append(key, deepLinkedParams[key]));
    }

    if (
      tmpRedirect.protocol === 'https:' ||
      (tmpRedirect.protocol === 'http:' && tmpRedirect.host.indexOf('localhost') >= 0)
    ) {
      redirectUri = `${tmpRedirect.protocol}//${tmpRedirect.host}${tmpRedirect.pathname}`;
      if (new Array(tmpRedirect.searchParams.keys()).length > 0) {
        context = tmpRedirect.search;
      }
    }

    const redirect = new URL(redirectUri);
    if (context) {
      redirect.searchParams.append('context', context);
    }

    if (queryParams) {
      Object.keys(queryParams).forEach(key => redirect.searchParams.append(key, queryParams[key]));
    }

    return redirect.toString();
  } catch (e: unknown) {
    actually.warn('could not parse redirect_uri');
    return defaultRedirect;
  }
};

export const getSignInRedirectUrl = (queryParams?: Record<string, string>) => {
  const signInRedirectUri = `${getOrigin()}${IDENTITY_WIDGET_BASEPATH}${IDENTITY_WIDGET_PATHS.SignInOrJoin}`;

  try {
    const redirectUri = new URL(window.location.href);

    if (queryParams) {
      Object.keys(queryParams).forEach(key => redirectUri.searchParams.append(key, queryParams[key]));
    }

    return `${signInRedirectUri}?redirect_uri=${redirectUri.toString()}`;
  } catch (e: unknown) {
    actually.warn('could not parse redirect_uri');
    return signInRedirectUri;
  }
};

export const useAppRedirect = () => {
  const redirectToApp = useCallback((queryParams?: Record<string, string>) => {
    const redirectUri = getRedirectUrl(queryParams);

    clearPersistedParameters();
    window.location.href = redirectUri;
  }, []);

  return redirectToApp;
};

export const useAppSignInRedirect = () => {
  const redirectToAppSignIn = useCallback((queryParams?: Record<string, string>) => {
    const redirectUri = getSignInRedirectUrl(queryParams);

    window.location.href = redirectUri;
  }, []);

  return redirectToAppSignIn;
};
